import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";

import {
    clearMessage,
    loginAsync,
    selectMessage,
    setMessage,
} from "../features/auth/authSlice";
import authService from "../services/auth.service";

const Login = (props) => {
    // const [familyName, setFamilyName] = useState("");
    // const [username, setUsername] = useState("");
    // const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);

    const form = useRef();
    const checkBtn = useRef();

    const resetPswDialog = useRef(null);

    const openDialog = () => {
        resetPswDialog.current.showModal();
    };

    const closeDialog = () => {
        resetPswDialog.current.close();
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const message = useSelector(selectMessage);

    useEffect(() => {
        dispatch(clearMessage());

        const savedFamilyName = getRememberedFamilyName();
        if (savedFamilyName) {
            formik.setValues({ ...formik.values, familyname: savedFamilyName });
        }
        setRememberMe(true);
        // Attempt to pre-fill username and password from browser's password manager
        tryToAutoFillCredentials();
    }, []);

    const setRememberedFamilyName = (familyName) => {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 30); // Expires in 30 days
        const cookieValue = JSON.stringify({ familyName });
        // console.log("Cookie to be set:", cookieValue);
        document.cookie = `rememberedCredentials=${encodeURIComponent(
            cookieValue
        )}; expires=${expirationDate.toUTCString()}; path=/; secure; SameSite=Strict`;
    };

    const getRememberedFamilyName = () => {
        const cookieString = document.cookie
            .split(";")
            .find((cookie) =>
                cookie.trim().startsWith("rememberedCredentials=")
            );
        if (cookieString) {
            const cookieValue = cookieString.split("=")[1];
            try {
                const data = JSON.parse(decodeURIComponent(cookieValue));
                return data.familyName;
            } catch (error) {
                console.error("Error parsing cookie data:", error);
            }
        }
        return "";
    };

    const tryToAutoFillCredentials = () => {
        // You can trigger the browser's password manager autofill feature
        // by setting the "name" attribute of the input fields to specific values.
        // Replace "yourUsernameField" and "yourPasswordField" with the actual field names.
        const usernameField = document.getElementsByName("username")[0];
        const passwordField = document.getElementsByName("password")[0];

        // Check if the fields exist and trigger autofill if possible
        if (usernameField && passwordField) {
            usernameField.setAttribute("autoComplete", "username");
            passwordField.setAttribute("autoComplete", "current-password");
        }
    };

    const validationSchema = Yup.object().shape({
        familyname: Yup.string().required("Familyname is required"),
        username: Yup.string().required("Username is required"),
        password: Yup.string().required("Password is required"),
    });

    const formik = useFormik({
        initialValues: {
            familyname: "",
            username: "",
            password: "",
        },
        validationSchema,
        // validateOnChange: false,
        // validateOnBlur: false,
        onSubmit: (user) => {
            setLoading(true);

            dispatch(loginAsync(user))
                .then((response) => {
                    if (response.type === "auth/login/fulfilled") {
                        if (rememberMe) {
                            // If "Remember Me" checkbox is checked, store the familyName in a cookie
                            setRememberedFamilyName(formik.values.familyname);
                        } else {
                            // If "Remember Me" is not checked, clear the familyName cookie
                            document.cookie =
                                "familyName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure;";
                        }

                        navigate("/");
                        window.location.reload();
                    } else {
                        setLoading(false);
                    }
                })
                .catch(() => {
                    setLoading(false);
                });
        },
    });

    const resetPswValidationSchema = Yup.object().shape({
        email: Yup.string()
            .required("Email is required")
            .email("Email is invalid"),
    });

    const resetPswFormik = useFormik({
        initialValues: {
            email: "",
        },
        // validateOnChange: false,
        // validateOnBlur: false,
        resetPswValidationSchema,
        onSubmit: () => {
            let email = resetPswFormik.values.email;
            if (!email) {
                return;
            } else {
                // make the reset password request to the api
                authService
                    .resetPsw(email)
                    .then((response) => {
                        if (response) {
                            console.log(response.data);
                            // if successfull close dialog
                            closeDialog();
                        } else {
                            console.log("No data retrieved");
                        }
                    })
                    .catch((e) => {
                        console.log(e);
                    });
            }
        },
        onReset: () => {
            closeDialog();
        },
    });

    const [pswType, setPswType] = useState("password");

    const toggleShowPsw = () => {
        if (pswType === "password") {
            setPswType("text");
        } else {
            setPswType("password");
        }
    };

    return (
        <div className="login-wrap">
            <div className="card card-container">
                <img
                    src="//ssl.gstatic.com/accounts/ui/avatar_2x.png"
                    alt="profile-img"
                    className="profile-img-card"
                />

                <form onSubmit={formik.handleSubmit} ref={form}>
                    <div>
                        <div className="form-group">
                            <label htmlFor="familyname"> Familyname </label>
                            <input
                                name="familyname"
                                type="text"
                                className={
                                    "form-control" +
                                    (formik.errors.familyname &&
                                    formik.touched.familyname
                                        ? " is-invalid"
                                        : "")
                                }
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur} // Add onBlur to trigger validation
                                value={formik.values.familyname}
                            />
                            <div className="invalid-feedback">
                                {formik.errors.familyname &&
                                formik.touched.familyname
                                    ? formik.errors.familyname
                                    : null}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="username"> Username </label>
                            <input
                                name="username"
                                type="text"
                                className={
                                    "form-control" +
                                    (formik.errors.username &&
                                    formik.touched.username
                                        ? " is-invalid"
                                        : "")
                                }
                                onChange={formik.handleChange}
                                value={formik.values.username}
                            />
                            <div className="invalid-feedback">
                                {formik.errors.username &&
                                formik.touched.username
                                    ? formik.errors.username
                                    : null}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="password"> Password </label>
                            <div className="input-group">
                                <input
                                    name="password"
                                    type={pswType}
                                    className={
                                        "form-control" +
                                        (formik.errors.password &&
                                        formik.touched.password
                                            ? " is-invalid"
                                            : "")
                                    }
                                    onChange={formik.handleChange}
                                    value={formik.values.password}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={toggleShowPsw}
                                    style={{ borderRadius: "0 6px 6px 0" }}
                                >
                                    {pswType === "password" ? (
                                        <i class="bi bi-eye" />
                                    ) : (
                                        <i class="bi bi-eye-slash" />
                                    )}
                                </button>
                                <div className="invalid-feedback">
                                    {formik.errors.password &&
                                    formik.touched.password
                                        ? formik.errors.password
                                        : null}
                                </div>
                            </div>
                        </div>

                        <div
                            onClick={openDialog}
                            style={{
                                cursor: "pointer",
                                fontSize: "0.8rem",
                                fontStyle: "italic",
                                color: "grey",
                                textAlign: "right",
                            }}
                        >
                            Forgot your password?
                        </div>

                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={rememberMe}
                                name="rememberMe"
                                onChange={(e) => {
                                    setRememberMe(e.target.checked);
                                    console.log(e.target.checked);
                                }}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="rememberMe"
                            >
                                Remember me
                            </label>
                        </div>

                        <hr></hr>

                        <div className="form-group">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading && (
                                    <span className="spinner-border spinner-border-sm"></span>
                                )}
                                <span>Login</span>
                            </button>{" "}
                            <button
                                type="button"
                                className="btn btn-outline-secondary float-right"
                                onClick={formik.handleReset}
                            >
                                Reset
                            </button>
                        </div>
                        {message && (
                            <div className="form-group">
                                <div
                                    className="alert alert-danger"
                                    role="alert"
                                >
                                    {message}
                                </div>
                            </div>
                        )}
                        <button
                            style={{ display: "none" }}
                            ref={checkBtn}
                        ></button>
                    </div>
                </form>

                <dialog className="custom-dialog" ref={resetPswDialog}>
                    <form onSubmit={resetPswFormik.handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="email">
                                Please insert your email address:
                            </label>
                            <br />
                            <input
                                name="email"
                                type="email"
                                className={
                                    "form-control" +
                                    (resetPswFormik.errors.email &&
                                    resetPswFormik.touched.email
                                        ? " is-invalid"
                                        : "")
                                }
                                onChange={resetPswFormik.handleChange}
                                value={resetPswFormik.values.email}
                            />
                            <div className="invalid-feedback">
                                {resetPswFormik.errors.email &&
                                resetPswFormik.touched.email
                                    ? resetPswFormik.errors.email
                                    : null}
                            </div>
                        </div>

                        <br />

                        <div className="form-group">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={resetPswFormik.handleReset}
                            >
                                <i className="bi bi-arrow-left-square" /> Back
                            </button>{" "}
                            <button
                                type="submit"
                                className="btn btn btn-sm btn-outline-primary"
                            >
                                <i className="bi bi-send" /> Reset password
                            </button>
                        </div>
                    </form>
                </dialog>
            </div>
        </div>
    );
};

export default Login;
